"use client";

import { FC, useState } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import noise from "@/assets/noise.png";
import { PrismicNextImage } from "@prismicio/next";
import Image from "next/image";
import KeyFacilityShape from "@/assets/KeyFacilityShape.svg";
import { motion } from "framer-motion";

/**
 * Props for `KeyFacilitiesSection`.
 */
export type KeyFacilitiesSectionProps =
  SliceComponentProps<Content.KeyFacilitiesSectionSlice>;

/**
 * Component for "KeyFacilitiesSection" Slices.
 */
const KeyFacilitiesSection: FC<KeyFacilitiesSectionProps> = ({ slice }) => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#FAFDFF] py-[120px]"
    >
      <Bounded as="div" className="container mx-auto">
        <div className="flex flex-col items-center justify-center">
          <motion.div
            className="font-archivo font-semibold text-5xl leading-16 text-[#033D61] mb-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 1 }}
          >
            <PrismicRichText
              field={slice.primary.title}
              components={{
                label: ({ node, children }) => {
                  if (node.data.label === "Highlight") {
                    return <span className="text-secondary">{children}</span>;
                  }
                  return <span className="text-primary">{children}</span>;
                },
              }}
            />
          </motion.div>
          <motion.div
            className="font-merriweather font-normal text-xl leading-8 text-center text-[#033D61] mb-10.5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <PrismicRichText field={slice.primary.description} />
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 items-center justify-center gap-4">
            {slice.primary.contents.map((content, index) => (
              <motion.div
                key={index}
                className="h-full"
                onHoverStart={() => {
                  setHoveredCard(index);
                }}
                onHoverEnd={() => {
                  setHoveredCard(null);
                }}
              >
                <motion.div
                  className="relative h-full transform-3d perspective-[1000px]"
                  animate={{
                    rotateY: hoveredCard === index ? 180 : 0,
                  }}
                  transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                >
                  <div className="rounded-[10px] px-9 py-10.5 h-full bg-white [box-shadow:0_10px_16px_0_rgba(7,152,242,0.10)] backface-hidden overflow-hidden">
                    <div className="relative">
                      <PrismicNextImage field={content.icon} className="mb-8" />
                      <h3 className="font-archivo text-3xl font-normal leading-10 mb-3 text-[#033D61]">
                        {content.title}
                      </h3>
                      <div className="font-merriweather text-lg font-normal leading-7 text-[#033D61]">
                        <PrismicRichText field={content.description} />
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 backface-hidden rotate-y-180 overflow-hidden rounded-[10px]">
                    <div className="px-9 py-10.5 relative bg-[#045B91] [box-shadow:0_16px_10px_0_rgba(7,152,242,0.10)]">
                      <div
                        className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.4]"
                        style={{
                          background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
                        }}
                      />
                      <Image
                        src={KeyFacilityShape.src}
                        alt="shape"
                        width={473}
                        height={439}
                        className="absolute top-0 right-0 h-[303px] w-auto pointer-events-none select-none"
                      />
                      <div className="relative">
                        <PrismicNextImage
                          field={content.reverted_icon}
                          className="mb-8"
                        />
                        <h3 className="font-archivo text-3xl font-normal leading-10 mb-3 text-[#EBF7FF]">
                          {content.title}
                        </h3>
                        <div className="font-merriweather text-lg font-normal leading-7 text-[#EBF7FF]">
                          <PrismicRichText field={content.description} />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </Bounded>
    </section>
  );
};

export default KeyFacilitiesSection;
